import { Link } from 'react-router-dom';
import PageHeader from '@/components/base/PageHeader';
import useCanonical from '@/hooks/useCanonical';
import Reveal from '@/components/feature/Reveal';
import { openSupportChat } from '@/utils/supportChat';
import { faqItems } from '@/mocks/faq';
import FaqAccordion from './components/FaqAccordion';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

const CANONICAL_URL = '/faq';
const PAGE_TITLE = 'GoldWarden EA FAQ — Free XAUUSD MT5 Expert Advisor Questions';
const PAGE_DESCRIPTION =
  'Answers traders ask before running the free GoldWarden XAUUSD EA — cost, minimum balance, drawdown, MT4 vs MT5, prop firm rules, demo testing, VPS and setup.';
const OG_IMAGE =
  'https://readdy.ai/api/search-image?query=Abstract%20dark%20financial%20banner%20with%20a%20glowing%20golden%20shield%20emblem%20and%20an%20organized%20grid%20of%20question%20mark%20motifs%2C%20deep%20charcoal%20green%20to%20black%20gradient%2C%20molten%20gold%20accents%2C%20cinematic%20depth%20of%20field%2C%20minimal%20futuristic%20fintech%20aesthetic%2C%20no%20text%2C%20wide%20editorial%20composition&width=1200&height=630&seq=og-faq-goldwarden-01&orientation=landscape&nocache=true';

// Quick links shown in the sidebar so visitors can jump straight to a topic.
const QUICK_LINKS = [
  { to: '/performance', icon: 'ri-line-chart-line', label: 'Live performance record', hint: 'Verified account stats' },
  { to: '/get-ea', icon: 'ri-download-2-line', label: 'Get the EA free', hint: 'Claim it in the chat' },
  { to: '/how-it-works', icon: 'ri-flow-chart', label: 'How the strategy works', hint: 'Entry, stops and exits' },
];

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: '/' },
    { '@type': 'ListItem', position: 2, name: 'FAQ', item: CANONICAL_URL },
  ],
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${CANONICAL_URL}#webpage`,
  name: 'GoldWarden EA frequently asked questions',
  description: PAGE_DESCRIPTION,
  url: CANONICAL_URL,
  inLanguage: 'en',
  isPartOf: { '@id': '/#website' },
  publisher: { '@id': '/#organization' },
};

export default function Faq() {
  useCanonical(CANONICAL_URL);

  return (
    <>
      <title>{PAGE_TITLE}</title>
      <meta name="description" content={PAGE_DESCRIPTION} />
      <meta name="keywords" content="free EA FAQ, XAUUSD MT5 expert advisor, MT4 vs MT5, prop firm EA, gold EA drawdown" />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="GoldWarden EA" />
      <meta property="og:title" content={PAGE_TITLE} />
      <meta property="og:description" content={PAGE_DESCRIPTION} />
      <meta property="og:url" content={CANONICAL_URL} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="GoldWarden EA frequently asked questions" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={PAGE_TITLE} />
      <meta name="twitter:description" content={PAGE_DESCRIPTION} />
      <meta name="twitter:image" content={OG_IMAGE} />
      <meta name="twitter:image:alt" content="GoldWarden EA frequently asked questions" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify([breadcrumbSchema, webPageSchema, faqSchema]) }}
      />

      <PageHeader
        breadcrumb="FAQ"
        eyebrow="Common questions"
        title="Everything people ask before they start"
        subtitle="Straight answers about cost, brokers, balance requirements and how the risk model actually works."
      />

      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-8">
          <div className="max-w-shell mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
            <div className="order-2 lg:order-1 lg:col-span-1">
              <div className="lg:sticky lg:top-28">
                <Reveal>
                  <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                  <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-950 text-accent-400">
                    <i className="ri-question-answer-line text-2xl" aria-hidden="true"></i>
                  </span>
                  <h2 className="mt-4 font-heading text-lg md:text-xl font-bold text-foreground-950">
                    Still have a question?
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                    Ask in the support chat. A real person answers questions about brokers, sizing and
                    installation — and hands out the EA itself.
                  </p>
                  <button
                    type="button"
                    onClick={openSupportChat}
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent-500 hover:bg-accent-600 px-6 py-3.5 text-sm font-semibold text-primary-950 transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-customer-service-2-line text-lg" aria-hidden="true"></i>
                    Ask a question
                  </button>

                  <div className="mt-6 border-t border-background-200 pt-5">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground-500">
                      Explore more
                    </p>
                    <nav className="mt-3 flex flex-col gap-1" aria-label="Quick links">
                      {QUICK_LINKS.map((link) => (
                        <Link
                          key={link.to}
                          to={link.to}
                          className="group flex items-center gap-3 rounded-md -mx-2 px-2 py-2 hover:bg-background-200/70 transition-colors"
                        >
                          <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-md bg-background-200 text-foreground-700 group-hover:bg-accent-100 group-hover:text-accent-800 transition-colors">
                            <i className={`${link.icon} text-base`} aria-hidden="true"></i>
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold text-foreground-900">{link.label}</span>
                            <span className="block text-xs text-foreground-500">{link.hint}</span>
                          </span>
                          <i
                            className="ri-arrow-right-line ml-auto text-foreground-400 group-hover:text-accent-700 transition-colors"
                            aria-hidden="true"
                          ></i>
                        </Link>
                      ))}
                    </nav>
                  </div>
                  </div>
                </Reveal>
              </div>
            </div>

            <div className="order-1 lg:order-2 lg:col-span-2">
              <Reveal>
                <FaqAccordion />
              </Reveal>

              <Reveal delay={120}>
                <div className="mt-8 rounded-lg bg-primary-950 p-6 md:p-8">
                  <h3 className="font-heading text-lg md:text-xl font-semibold text-background-50">
                    One honest warning before you start
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-background-200/85">
                    No expert advisor can guarantee profit, and anyone claiming otherwise is selling you
                    something. Trade gold with money you can afford to risk, start small, and let the
                    account compound over months rather than days.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}